import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import VideoPlayer from './components/VideoPlayer';
import VideoGrid from './components/VideoGrid';
import AdminLogin from './components/AdminLogin';
import UploadForm from './components/UploadForm';
import ContentSection from './components/ContentSection';
import Navbar from './components/Navbar';
import Analytics from './components/Analytics';
import Books from './components/Books';
import { guestClient, authClient } from './amplifyConfig';
import './styles/App.css';

function getVisitorId() {
  let id = localStorage.getItem('visitorId');
  if (!id) { id = crypto.randomUUID(); localStorage.setItem('visitorId', id); }
  return id;
}

async function trackEvent(eventType, contentId = null, contentTitle = null) {
  try {
    await guestClient.models.AnalyticsEvent.create({
      eventType, contentId, contentTitle,
      visitorId: getVisitorId(),
      timestamp: new Date().toISOString(),
    });
  } catch (e) {
    console.warn('Analytics error:', e);
  }
}

function AppInner() {
  const [videos, setVideos] = useState([]);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [poetry, setPoetry] = useState([]);
  const [featuredPoetry, setFeaturedPoetry] = useState(null);
  const [blog, setBlog] = useState([]);
  const [featuredBlog, setFeaturedBlog] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function init() {
      try {
        await getCurrentUser();
        setIsAdmin(true);
      } catch (_) {}

      try {
        const [videosRes, poetryRes, blogRes] = await Promise.all([
          guestClient.models.Video.list(),
          guestClient.models.Poetry.list(),
          guestClient.models.Blog.list(),
        ]);
        const videoItems = videosRes.data || [];
        const poetryItems = poetryRes.data || [];
        const blogItems = blogRes.data || [];

        setVideos(videoItems);
        setFeaturedVideo(videoItems.find(v => v.isFeatured) || videoItems[0] || null);
        setPoetry(poetryItems);
        setFeaturedPoetry(poetryItems.find(p => p.isFeatured) || null);
        setBlog(blogItems);
        setFeaturedBlog(blogItems.find(b => b.isFeatured) || null);
      } catch (e) {
        console.error('Failed to load content:', e);
      } finally {
        setLoading(false);
      }
    }
    init();
    trackEvent('page_visit');
  }, []);

  const handleUpload = async (newVideo) => {
    try {
      const res = await authClient.models.Video.create(newVideo);
      const created = res.data;
      setVideos(prev => [...prev, created]);
      if (!featuredVideo) setFeaturedVideo(created);
      setShowUpload(false);
    } catch (e) { console.error('Upload failed:', e); }
  };

  const handleSetFeatured = async (video) => {
    try {
      if (featuredVideo) await authClient.models.Video.update({ id: featuredVideo.id, isFeatured: false });
      await authClient.models.Video.update({ id: video.id, isFeatured: true });
      setVideos(prev => prev.map(v => ({ ...v, isFeatured: v.id === video.id })));
      setFeaturedVideo({ ...video, isFeatured: true });
    } catch (e) { console.error('Set featured failed:', e); }
  };

  const handleUpdateTitle = async (id, newTitle) => {
    try {
      await authClient.models.Video.update({ id, title: newTitle });
      setVideos(prev => prev.map(v => v.id === id ? { ...v, title: newTitle } : v));
      if (featuredVideo?.id === id) setFeaturedVideo(prev => ({ ...prev, title: newTitle }));
    } catch (e) { console.error('Update title failed:', e); }
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await authClient.models.Video.delete({ id });
      const remaining = videos.filter(v => v.id !== id);
      setVideos(remaining);
      if (featuredVideo?.id === id) setFeaturedVideo(remaining[0] || null);
    } catch (e) { console.error('Delete failed:', e); }
  };

  if (loading) {
    return (
      <div className="app" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ color: 'var(--text-dim)', letterSpacing: '3px', fontSize: '0.8rem' }}>LOADING...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <h1 className="site-title">Vlad's Corner</h1>
        <p className="site-subtitle">Music · Poetry · Philosophy · Books</p>
        <Navbar isAdmin={isAdmin} />
        <AdminLogin
          isAdmin={isAdmin}
          onLogin={() => setIsAdmin(true)}
          onLogout={() => setIsAdmin(false)}
        />
      </header>

      <main className="main">
        <Routes>
          <Route path="/" element={
            <>
              {featuredVideo && <VideoPlayer video={featuredVideo} onPlay={() => trackEvent('video_play', featuredVideo.id, featuredVideo.title)} />}
              {isAdmin && (
                <button className="upload-btn" onClick={() => setShowUpload(!showUpload)}>
                  {showUpload ? 'Cancel' : '+ Upload New Video'}
                </button>
              )}
              {showUpload && <UploadForm onUpload={handleUpload} onCancel={() => setShowUpload(false)} />}
              <VideoGrid
                videos={videos}
                onSetFeatured={handleSetFeatured}
                onUpdateTitle={handleUpdateTitle}
                onDelete={handleDeleteVideo}
                onCardClick={(v) => { setFeaturedVideo(v); trackEvent('video_card_click', v.id, v.title); }}
                isAdmin={isAdmin}
              />
            </>
          } />
          <Route path="/poetry" element={
            <ContentSection
              type="poetry"
              items={poetry}
              setItems={setPoetry}
              featured={featuredPoetry}
              setFeatured={setFeaturedPoetry}
              isAdmin={isAdmin}
              onCardClick={(item) => trackEvent('poetry_click', item.id, item.title)}
            />
          } />
          <Route path="/blog" element={
            <ContentSection
              type="blog"
              items={blog}
              setItems={setBlog}
              featured={featuredBlog}
              setFeatured={setFeaturedBlog}
              isAdmin={isAdmin}
              onCardClick={(item) => trackEvent('blog_click', item.id, item.title)}
            />
          } />
          <Route path="/books" element={<Books />} />
          {isAdmin && <Route path="/analytics" element={<Analytics />} />}
        </Routes>
      </main>

      <footer className="footer">
        <p>© {new Date().getFullYear()} Vlad's Corner</p>
        <a href="mailto:vlad@vlads-corner.awsapps.com" className="footer-email">vlad@vlads-corner.awsapps.com</a>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

export default App;
